import React from 'react';
import LegalHeader from './LegalHeader';

const CookiesPolicyView = ({ onBack }) => {
  return (
    <div className="min-h-screen bg-black text-white">
      <LegalHeader />
      <div className="max-w-3xl mx-auto px-4 py-8">
        {onBack && (
          <button
            onClick={onBack}
            className="mb-6 text-[#22c55e] hover:text-[#16a34a] flex items-center space-x-1 text-sm"
          >
            <span>← Back</span>
          </button>
        )}

        <h1 className="text-3xl font-bold text-white mb-1">Cookies Policy</h1>
        <p className="text-gray-400 text-sm mb-1">Effective Date: April 8, 2026</p>
        <p className="text-gray-400 text-sm mb-8">Last Updated: April 8, 2026</p>

        <div className="prose prose-invert max-w-none space-y-8 text-sm leading-relaxed text-gray-300">

          <section>
            <h2 className="text-lg font-bold text-white mb-3">What Are Cookies?</h2>
            <p>Cookies are small text files stored on your device when you visit a website. They help the site remember information about your visit, making your experience smoother and more personalized. Some cookies are essential for the site to function, while others help us understand how you use the app.</p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-white mb-3">Who We Are</h2>
            <p>Loft ("we," "us," or "our") operates the golf social networking platform at loftgolf.app. This policy explains what cookies and similar technologies we use, why we use them, and how you can control them.</p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-white mb-3">What Cookies We Use</h2>

            <h3 className="text-base font-semibold text-white mb-2 mt-4">1. Essential Cookies</h3>
            <p className="mb-3">These cookies are necessary for Loft to function and cannot be turned off. They are set in response to actions you take, such as logging in or adjusting your privacy settings.</p>
            <div className="overflow-x-auto">
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="border-b border-gray-700">
                    <th className="text-left py-2 pr-4 text-white font-semibold">Cookie</th>
                    <th className="text-left py-2 pr-4 text-white font-semibold">Provider</th>
                    <th className="text-left py-2 text-white font-semibold">Purpose</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-gray-800">
                    <td className="py-2 pr-4">Firebase Auth session</td>
                    <td className="py-2 pr-4">Google Firebase</td>
                    <td className="py-2">Keeps you logged in securely across sessions</td>
                  </tr>
                  <tr>
                    <td className="py-2 pr-4">Firebase session ID</td>
                    <td className="py-2 pr-4">Google Firebase</td>
                    <td className="py-2">Manages your active session and authentication state</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <h3 className="text-base font-semibold text-white mb-2 mt-6">2. Payment Cookies</h3>
            <p className="mb-3">When you initiate an upgrade to Caddie Pro, you will interact with Stripe's secure checkout environment. Stripe sets cookies to process payments securely, prevent fraud, and remember your checkout state.</p>
            <div className="overflow-x-auto">
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="border-b border-gray-700">
                    <th className="text-left py-2 pr-4 text-white font-semibold">Cookie</th>
                    <th className="text-left py-2 pr-4 text-white font-semibold">Provider</th>
                    <th className="text-left py-2 text-white font-semibold">Purpose</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-gray-800">
                    <td className="py-2 pr-4">__stripe_mid</td>
                    <td className="py-2 pr-4">Stripe</td>
                    <td className="py-2">Fraud prevention and secure payment processing</td>
                  </tr>
                  <tr>
                    <td className="py-2 pr-4">__stripe_sid</td>
                    <td className="py-2 pr-4">Stripe</td>
                    <td className="py-2">Maintains your session during checkout</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="mt-3">For more information on Stripe's data practices, visit <a href="https://stripe.com/privacy" target="_blank" rel="noopener noreferrer" className="text-[#22c55e] hover:underline">stripe.com/privacy</a>.</p>

            <h3 className="text-base font-semibold text-white mb-2 mt-6">3. Analytics Cookies</h3>
            <p className="mb-3">We use Google Analytics to understand how visitors use Loft — which features are popular, how users navigate the app, and where we can improve. This data is aggregated and anonymous.</p>
            <div className="overflow-x-auto">
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="border-b border-gray-700">
                    <th className="text-left py-2 pr-4 text-white font-semibold">Cookie</th>
                    <th className="text-left py-2 pr-4 text-white font-semibold">Provider</th>
                    <th className="text-left py-2 pr-4 text-white font-semibold">Purpose</th>
                    <th className="text-left py-2 text-white font-semibold">Duration</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-gray-800">
                    <td className="py-2 pr-4">_ga</td>
                    <td className="py-2 pr-4">Google Analytics</td>
                    <td className="py-2 pr-4">Distinguishes unique users</td>
                    <td className="py-2">2 years</td>
                  </tr>
                  <tr className="border-b border-gray-800">
                    <td className="py-2 pr-4">_ga_*</td>
                    <td className="py-2 pr-4">Google Analytics</td>
                    <td className="py-2 pr-4">Maintains session state</td>
                    <td className="py-2">2 years</td>
                  </tr>
                  <tr className="border-b border-gray-800">
                    <td className="py-2 pr-4">_gid</td>
                    <td className="py-2 pr-4">Google Analytics</td>
                    <td className="py-2 pr-4">Distinguishes users (session)</td>
                    <td className="py-2">24 hours</td>
                  </tr>
                  <tr>
                    <td className="py-2 pr-4">_gat</td>
                    <td className="py-2 pr-4">Google Analytics</td>
                    <td className="py-2 pr-4">Throttles request rate</td>
                    <td className="py-2">1 minute</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="mt-3">You can opt out of Google Analytics tracking at any time using the <a href="https://tools.google.com/dlpage/gaoptout" target="_blank" rel="noopener noreferrer" className="text-[#22c55e] hover:underline">Google Analytics Opt-out Browser Add-on</a>.</p>

            <h3 className="text-base font-semibold text-white mb-2 mt-6">4. Progressive Web App (PWA) Storage</h3>
            <p>As a PWA, Loft uses your browser's local storage and service worker cache to enable offline functionality and faster load times. This is not a cookie in the traditional sense, but it does store data on your device, including cached app assets and your user preferences.</p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-white mb-3">How Long Do Cookies Last?</h2>
            <ul className="list-disc list-inside space-y-1 ml-2">
              <li><strong className="text-white">Session cookies</strong> expire when you close your browser</li>
              <li><strong className="text-white">Persistent cookies</strong> remain on your device until they expire or you delete them</li>
              <li>Specific durations are listed in the tables above</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-bold text-white mb-3">How to Control Cookies</h2>
            <p className="mb-3">You have several options for managing cookies:</p>
            <p className="mb-2"><strong className="text-white">Browser settings:</strong> Most browsers allow you to block or delete cookies through their settings. Note that blocking essential cookies will prevent Loft from working properly.</p>
            <ul className="list-disc list-inside space-y-1 ml-2 mb-4">
              <li><a href="https://support.google.com/chrome/answer/95647" target="_blank" rel="noopener noreferrer" className="text-[#22c55e] hover:underline">Chrome</a></li>
              <li><a href="https://support.apple.com/guide/safari/manage-cookies-sfri11471/mac" target="_blank" rel="noopener noreferrer" className="text-[#22c55e] hover:underline">Safari</a></li>
              <li><a href="https://support.mozilla.org/en-US/kb/cookies-information-websites-store-on-your-computer" target="_blank" rel="noopener noreferrer" className="text-[#22c55e] hover:underline">Firefox</a></li>
            </ul>
            <p className="mb-2"><strong className="text-white">Google Analytics opt-out:</strong> Use the <a href="https://tools.google.com/dlpage/gaoptout" target="_blank" rel="noopener noreferrer" className="text-[#22c55e] hover:underline">Google Analytics Opt-out Add-on</a> to prevent your data from being used by Google Analytics across all websites.</p>
            <p><strong className="text-white">Stripe:</strong> Stripe's cookies are only active during payment sessions. You can review Stripe's cookie practices at <a href="https://stripe.com/legal/cookies-policy" target="_blank" rel="noopener noreferrer" className="text-[#22c55e] hover:underline">stripe.com/legal/cookies-policy</a>.</p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-white mb-3">Third-Party Cookies</h2>
            <p className="mb-3">Some cookies on Loft are set by third-party services we use. We do not control these third parties' cookies and recommend reviewing their privacy policies directly:</p>
            <ul className="list-disc list-inside space-y-1 ml-2">
              <li><strong className="text-white">Google Firebase:</strong> <a href="https://firebase.google.com/support/privacy" target="_blank" rel="noopener noreferrer" className="text-[#22c55e] hover:underline">firebase.google.com/support/privacy</a></li>
              <li><strong className="text-white">Google Analytics:</strong> <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="text-[#22c55e] hover:underline">policies.google.com/privacy</a></li>
              <li><strong className="text-white">Stripe:</strong> <a href="https://stripe.com/privacy" target="_blank" rel="noopener noreferrer" className="text-[#22c55e] hover:underline">stripe.com/privacy</a></li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-bold text-white mb-3">Changes to This Policy</h2>
            <p>We may update this policy from time to time to reflect changes in our technology or legal requirements. When we do, we will update the "Last Updated" date at the top of this page. We encourage you to review this page periodically.</p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-white mb-3">Contact Us</h2>
            <p>If you have questions about our use of cookies, please contact us at:</p>
            <p className="mt-2"><strong className="text-white">Email:</strong> <a href="mailto:contact@loftgolf.app" className="text-[#22c55e] hover:underline">contact@loftgolf.app</a></p>
            <p><strong className="text-white">Website:</strong> loftgolf.app</p>
          </section>

        </div>
        <div style={{ height: '28rem' }} />
      </div>
    </div>
  );
};

export default CookiesPolicyView;
